import React from 'react';
import { Box, Button, Typography, IconButton, CircularProgress } from '@mui/material';
import { ArrowBack, Save, Close, Delete, Star, StarBorder, Settings } from '@mui/icons-material';

/**
 * Reusable Form Header Component
 * Provides consistent header for all form pages (CustomerForm, ProductForm, InvoiceForm, etc.)
 * 
 * @param {string} title - Form title
 * @param {boolean} isEdit - Whether this is edit mode
 * @param {boolean} saving - Whether form is currently saving
 * @param {boolean} loading - Whether form is loading
 * @param {function} onSave - Callback when save button is clicked
 * @param {function} onBack - Callback when back button is clicked
 * @param {function} onCancel - Callback when cancel button is clicked
 * @param {function} onDelete - Callback when delete button is clicked
 * @param {boolean} showDelete - Whether to show delete button
 * @param {boolean} canDelete - Whether user can delete
 * @param {boolean} starred - Whether item is starred/favorited
 * @param {function} onToggleStar - Callback to toggle star
 * @param {boolean} showStar - Whether to show star button
 * @param {React.ReactNode} additionalActions - Additional action buttons
 * @param {string} primaryColor - Primary button color
 * @param {object} sx - Additional styles
 */
const FormHeader = ({
  title,
  isEdit = false,
  saving = false,
  loading = false,
  onSave,
  onBack,
  onCancel,
  onDelete,
  showDelete = false,
  canDelete = true,
  starred = false,
  onToggleStar,
  showStar = false,
  additionalActions,
  primaryColor = '#875A7B',
  saveText = 'Save',
  cancelText = 'Discard',
  sx = {},
}) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'nowrap',
        ...sx,
      }}
    >
      {/* Left side - Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Save Button */}
        {onSave && (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: primaryColor,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2,
              py: 0.5,
              minHeight: 32,
              '&:hover': { backgroundColor: adjustColor(primaryColor, -15) },
              '&.Mui-disabled': { backgroundColor: '#ccc' },
            }}
          >
            {saving ? 'Saving...' : saveText}
          </Button>
        )}

        {/* Discard/Cancel Button */}
        {(onCancel || onBack) && (
          <Button
            variant="outlined"
            onClick={onCancel || onBack}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2,
              py: 0.5,
              minHeight: 32,
              color: '#666',
              borderColor: '#ccc',
              '&:hover': { borderColor: '#999', bgcolor: 'transparent' },
            }}
          >
            {cancelText}
          </Button>
        )}
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#333',
          flex: 1,
        }}
      >
        {title || (isEdit ? 'Edit' : 'New')}
      </Typography>

      {/* Right side - Additional actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Star/Favorite button */}
        {showStar && onToggleStar && (
          <IconButton
            size="small"
            onClick={onToggleStar}
            sx={{ color: starred ? '#ffc107' : '#ccc' }}
          >
            {starred ? <Star /> : <StarBorder />}
          </IconButton>
        )}

        {/* Additional actions */}
        {additionalActions}

        {/* Delete button */}
        {showDelete && onDelete && canDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{ color: '#dc3545' }}
          >
            <Delete />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

// Helper function to adjust color brightness
const adjustColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

export default FormHeader;
